import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  getCookiePrefs,
  hasDecidedCookies,
  setCookiePrefs,
} from "@/lib/cookie-prefs";
import { supabase } from "@/integrations/supabase/client";
import { Cookie, X } from "lucide-react";

/**
 * LGPD-compliant cookie banner.
 *
 * Appears on first visit until the user makes a choice. Offers three paths:
 *  - Accept all (analytics + marketing on)
 *  - Reject non-essential (only functional)
 *  - Customize (switches)
 *
 * The decision is stored locally, and also synced to
 * `user_cookie_preferences` when the user is authenticated.
 */
export function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (!hasDecidedCookies()) {
      setOpen(true);
      const prefs = getCookiePrefs();
      setAnalytics(prefs.analytics);
      setMarketing(prefs.marketing);
    }
  }, []);

  const persist = async (a: boolean, m: boolean) => {
    setCookiePrefs({ analytics: a, marketing: m, preferences: true });
    try {
      const { data } = await supabase.auth.getUser();
      if (data?.user) {
        await supabase.from("user_cookie_preferences").upsert(
          {
            user_id: data.user.id,
            analytics: a,
            marketing: m,
            preferences: true,
          },
          { onConflict: "user_id" },
        );
      }
    } catch (err) {
      // Non-blocking: the local decision is what protects analytics gating.
      console.warn("[CookieBanner] sync failed", err);
    }
    setOpen(false);
  };

  const acceptAll = () => persist(true, true);
  const rejectAll = () => persist(false, false);
  const saveCustom = () => persist(analytics, marketing);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-label="Preferências de cookies"
      className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 sm:px-6 sm:pb-6"
    >
      <div className="mx-auto max-w-3xl rounded-xl border border-border bg-card shadow-lg">
        <div className="flex items-start gap-3 p-4 sm:p-5">
          <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Cookie className="h-5 w-5" />
          </div>

          <div className="flex-1 space-y-3">
            <div>
              <h2 className="text-sm font-semibold">
                Usamos cookies para melhorar sua experiência
              </h2>
              <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                Cookies essenciais mantêm a plataforma funcionando (login,
                segurança). Os demais são opcionais e você pode recusar sem
                impacto no uso. Saiba mais em{" "}
                <a
                  href="/legal/cookies"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-foreground"
                >
                  Política de Cookies
                </a>
                .
              </p>
            </div>

            {showDetails && (
              <div className="space-y-3 rounded-md border border-border/60 bg-muted/30 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Label className="text-xs font-medium">Essenciais</Label>
                    <p className="text-[11px] text-muted-foreground">
                      Necessários para autenticação, segurança e funcionamento
                      básico. Sempre ativos.
                    </p>
                  </div>
                  <Switch checked disabled />
                </div>

                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Label htmlFor="ck-analytics" className="text-xs font-medium">
                      Analytics
                    </Label>
                    <p className="text-[11px] text-muted-foreground">
                      Medição anonimizada de uso para melhorar a experiência
                      (PostHog).
                    </p>
                  </div>
                  <Switch
                    id="ck-analytics"
                    checked={analytics}
                    onCheckedChange={setAnalytics}
                  />
                </div>

                <div className="flex items-start justify-between gap-3">
                  <div>
                    <Label htmlFor="ck-marketing" className="text-xs font-medium">
                      Marketing
                    </Label>
                    <p className="text-[11px] text-muted-foreground">
                      Comunicações e anúncios personalizados. Atualmente não
                      utilizamos — reservado para o futuro.
                    </p>
                  </div>
                  <Switch
                    id="ck-marketing"
                    checked={marketing}
                    onCheckedChange={setMarketing}
                  />
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={acceptAll}>
                Aceitar todos
              </Button>
              <Button size="sm" variant="outline" onClick={rejectAll}>
                Rejeitar não essenciais
              </Button>
              {showDetails ? (
                <Button size="sm" variant="secondary" onClick={saveCustom}>
                  Salvar preferências
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowDetails(true)}
                >
                  Personalizar
                </Button>
              )}
            </div>
          </div>

          <button
            type="button"
            aria-label="Fechar"
            onClick={rejectAll}
            className="rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
